import { z } from 'zod';
import { event_categories } from '../constants';

// Create a type for valid categories
const validCategories = event_categories.map((cat) => cat.name);
type EventCategory = (typeof validCategories)[number];

// Define valid event statuses
export const EVENT_STATUS = {
	PENDING: 'pending',
	APPROVED: 'approved',
	REJECTED: 'rejected',
} as const;

export type EventStatus = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS];

// Define RSVP question types
export const RSVP_QUESTION_TYPES = {
	TEXT: 'text',
	TEXTAREA: 'textarea',
	SELECT: 'select',
	MULTIPLE_CHOICE: 'multiple_choice',
	BOOLEAN: 'boolean',
	SECTION_HEADER: 'section_header',
} as const;

export type RsvpQuestionType =
	(typeof RSVP_QUESTION_TYPES)[keyof typeof RSVP_QUESTION_TYPES];

export const rsvpQuestionSchema = z.object({
	id: z.string().optional(),
	question: z.string().min(1, { message: 'Question text is required' }),
	type: z.enum([
		RSVP_QUESTION_TYPES.TEXT,
		RSVP_QUESTION_TYPES.TEXTAREA,
		RSVP_QUESTION_TYPES.SELECT,
		RSVP_QUESTION_TYPES.MULTIPLE_CHOICE,
		RSVP_QUESTION_TYPES.BOOLEAN,
		RSVP_QUESTION_TYPES.SECTION_HEADER,
	]),
	options: z.array(z.string()).optional().nullable(),
	required: z.boolean().default(false),
	order: z.number().default(0),
	description: z.string().optional().nullable(), // For section headers
});

export const rsvpResponseSchema = z.object({
	question_id: z.string().uuid(),
	response: z.string(),
});

export const ticketTypeSchema = z.object({
	id: z.string().uuid().optional(),
	name: z.string().min(2, { message: 'Ticket name is required' }),
	price: z.number().min(0, { message: 'Price must be 0 or greater' }),
	quantity: z.number().min(1, { message: 'Must have at least 1 ticket' }),
});

export const eventFormSchema = z.object({
	name: z.string().min(2, { message: 'Name is required' }),
	description: z.string().min(2, { message: 'Description is required' }),
	organization_id: z.string().uuid({ message: 'Organization ID is required' }),
	category: z.enum(validCategories as [string, ...string[]], {
		required_error: 'Please select a category',
	}),
	start_date: z.coerce.date(),
	time: z.string().min(2, { message: 'Time is required' }),
	end_time: z.string().min(2, { message: 'End time is required' }),
	venue: z.string().min(2, { message: 'Venue is required' }),
	location: z.string().min(2, { message: 'Location is required' }),
	image_url: z.string().url({ message: 'Image URL is required' }),
	tags: z.array(z.string()).min(1, { message: 'At least one tag is required' }),
	tickets: z
		.array(ticketTypeSchema)
		.min(1, { message: 'At least one ticket type is required' }),
	rsvpQuestions: z.array(rsvpQuestionSchema).default([]),
	status: z
		.enum([EVENT_STATUS.PENDING, EVENT_STATUS.APPROVED, EVENT_STATUS.REJECTED])
		.default(EVENT_STATUS.PENDING),
});

export type EventFormSchema = z.infer<typeof eventFormSchema>;
export type RsvpQuestion = z.infer<typeof rsvpQuestionSchema>;
export type RsvpResponse = z.infer<typeof rsvpResponseSchema>;
